/**
 * MapLayersPanel — the "Layers" overlay used by the multi-site map surfaces
 * (the evaluation Sites tab, the portfolio Locations map).
 *
 * Distinct from `MapControls`: Rings is not a single on/off toggle here but a
 * dropdown of independent per-radius checkboxes, so a user can show the 1-mile
 * ring without the 5-mile one. That shape does not fit the flat
 * `MapToggleControl[]` API, which is why this is its own component rather than
 * a `MapControls` variant.
 *
 * Positioning and chrome are the consumer's job — pass a `className` with the
 * absolute placement.
 */

import { ChevronDown } from 'lucide-react'
import { cn } from 'lib/utils'
import {
	DropdownMenu,
	DropdownMenuTrigger,
	DropdownMenuContent,
	DropdownMenuCheckboxItem,
} from 'components/ui/dropdown-menu'
import { TRADE_AREA_RINGS } from '../config/mapConfig'

const PILL_BASE =
	'inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-md text-xs font-medium border transition-colors'
const PILL_ACTIVE = 'bg-[#0080fe] text-white border-[#0080fe]'
const PILL_IDLE = 'bg-white text-gray-600 border-gray-200 hover:border-gray-300'

function TogglePill({
	label,
	active,
	onToggle,
}: {
	label: string
	active: boolean
	onToggle: () => void
}) {
	return (
		<button
			type="button"
			onClick={onToggle}
			aria-pressed={active}
			className={cn(PILL_BASE, active ? PILL_ACTIVE : PILL_IDLE)}
		>
			<span className={cn('w-2 h-2 rounded-full', active ? 'bg-white' : 'bg-gray-300')} />
			{label}
		</button>
	)
}

export interface MapLayersPanelProps {
	showMarkers: boolean
	onToggleMarkers: () => void
	/** The trade-area radii currently drawn. Empty means no rings. */
	enabledRadii: number[]
	onToggleRadius: (radius: number) => void
	showOverlaps: boolean
	onToggleOverlaps: () => void
	showDemographics: boolean
	onToggleDemographics: () => void
	/**
	 * Other Retailers. Optional, and the pill renders only when the handler is
	 * supplied: the index map and the portfolio tab have no anchor asset to
	 * scan around, so a toggle there would control nothing.
	 */
	showOtherRetailers?: boolean
	onToggleOtherRetailers?: () => void
	/**
	 * Trailing detail on that pill, e.g. "12 · 5 mi".
	 *
	 * Worth the space: when a ring is narrowing the layer, a user who does not
	 * know that has no way to tell a filtered map from an empty one.
	 */
	otherRetailersLabel?: string
	title?: string
	className?: string
}

export function MapLayersPanel({
	showMarkers,
	onToggleMarkers,
	enabledRadii,
	onToggleRadius,
	showOverlaps,
	onToggleOverlaps,
	showDemographics,
	onToggleDemographics,
	showOtherRetailers = false,
	onToggleOtherRetailers,
	otherRetailersLabel,
	title = 'Layers',
	className,
}: MapLayersPanelProps) {
	const ringsActive = enabledRadii.length > 0

	return (
		<div
			className={cn(
				'rounded-md border border-gray-200 bg-white/95 backdrop-blur shadow-sm px-3 py-2',
				className,
			)}
		>
			<h5 className="text-[10px] uppercase tracking-wide font-semibold text-gray-500 mb-1.5">
				{title}
			</h5>
			<div className="flex flex-col gap-1.5">
				<TogglePill label="Markers" active={showMarkers} onToggle={onToggleMarkers} />
				{/* Rings — each radius is its own toggle, so a 1-mile ring can be shown
				    without the 5-mile one. */}
				<DropdownMenu>
					<DropdownMenuTrigger asChild>
						<button
							type="button"
							className={cn(PILL_BASE, ringsActive ? PILL_ACTIVE : PILL_IDLE)}
						>
							<span
								className={cn('w-2 h-2 rounded-full', ringsActive ? 'bg-white' : 'bg-gray-300')}
							/>
							Rings
							<ChevronDown className="w-3 h-3 ml-auto" />
						</button>
					</DropdownMenuTrigger>
					<DropdownMenuContent align="start" side="right">
						{TRADE_AREA_RINGS.map((r) => (
							<DropdownMenuCheckboxItem
								key={r}
								checked={enabledRadii.includes(r)}
								onCheckedChange={() => onToggleRadius(r)}
							>
								{r} mile{r === 1 ? '' : 's'}
							</DropdownMenuCheckboxItem>
						))}
					</DropdownMenuContent>
				</DropdownMenu>
				<TogglePill label="Overlaps" active={showOverlaps} onToggle={onToggleOverlaps} />
				<TogglePill
					label="Demographics"
					active={showDemographics}
					onToggle={onToggleDemographics}
				/>
				{/* Only where there is an anchor asset to hang retailers off. */}
				{onToggleOtherRetailers && (
					<button
						type="button"
						onClick={onToggleOtherRetailers}
						aria-pressed={showOtherRetailers}
						className={cn(PILL_BASE, showOtherRetailers ? PILL_ACTIVE : PILL_IDLE)}
					>
						<span
							className={cn(
								'w-2 h-2 rounded-full',
								showOtherRetailers ? 'bg-white' : 'bg-gray-300',
							)}
						/>
						Other Retailers
						{otherRetailersLabel && (
							<span className="ml-auto pl-1 text-[10px] tabular-nums opacity-70">
								{otherRetailersLabel}
							</span>
						)}
					</button>
				)}
			</div>
		</div>
	)
}

export default MapLayersPanel
